import React from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { Box, Link, Paragraph, Text } from '@rover/kibble/core';
import UnorderedList, {
  ListItem,
} from '@rover/react-lib/src/components/lists/UnorderedList/UnorderedList';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import { RoverBrand } from '@rover/types/src/RoverBrand';
import getBrandRedirectUrl from '@rover/utilities/getBrandRedirectUrl';

import { FAQCategory, FAQCategoryType, FAQConfig } from '../types';

type GetCatInAFlatFAQConfigParams = {
  countryConfig?: CountryConfiguration | null;
  showSitter101?: boolean;
};

export const getCatInAFlatFAQConfig = ({
  countryConfig,
  showSitter101,
}: GetCatInAFlatFAQConfigParams): FAQConfig => {
  const serviceFeesUrl = countryConfig?.urls?.localizedLinkUrls['what-are-the-service-fees'] || '';
  const sitterCancellationPoliciesUrl =
    countryConfig?.urls?.localizedLinkUrls['what-are-the-sitter-cancellation-policies'] || '';
  const manageMyCalendarUrl =
    countryConfig?.urls?.localizedLinkUrls['how-do-i-manage-my-calendar'] || '';
  const improveMyPositionInSearchUrl =
    countryConfig?.urls?.localizedLinkUrls['how-do-i-improve-my-position-in-search-results'] ||
    'https://support.rover.com/hc/en-us/articles/202838454-How-do-I-improve-my-position-in-search-results';
  const roverCardsUrl =
    countryConfig?.urls?.localizedLinkUrls['what-are-rover-cards-and-how-do-i-send-them'] ||
    'https://support-uk.rover.com/hc/en-gb/articles/360015734172-What-are-Rover-Cards-and-how-do-I-send-them';
  const howDoIGetPaidForBookingsAndWhenUrl =
    countryConfig?.urls?.localizedLinkUrls['how-do-i-get-paid-for-bookings-and-when'] || '';
  const howDoIManageMyRatesForAdditionalServicesUrl =
    countryConfig?.urls?.localizedLinkUrls['how-do-i-manage-my-rates-for-additional-services'] ||
    '';
  const howDoIModifyAPendingRequestBeforeBookingItUrl =
    countryConfig?.urls?.localizedLinkUrls['how-do-i-modify-a-pending-request-before-booking-it'] ||
    '';
  const howDoIBookWithAnOwnerOrCreateARepeatBookingUrl =
    countryConfig?.urls?.localizedLinkUrls[
      'how-do-i-book-with-an-owner-or-create-a-repeat-booking'
    ] || '';
  const vetCareReimbursementAmount =
    countryConfig?.formattedVetCareReimbursementAmount || '$25,000';
  const termsOfServiceURL =
    countryConfig?.urls?.tosUrl || getBrandRedirectUrl(RoverBrand.Rover, '/terms/tos/');
  const roverGuaranteeURL = getBrandRedirectUrl(RoverBrand.Rover, '/rover-guarantee/');
  const roverBlogURL = getBrandRedirectUrl(RoverBrand.Rover, '/blog/');
  const sitterResourceCenterURL = getBrandRedirectUrl(RoverBrand.Rover, '/blog/sitter-resources/');
  const helpCenterURL = countryConfig?.urls?.supportUrl || 'https://support.rover.com/hc/en-us';
  const activationUrl = 'https://www.CatinaFlat.com/activation';
  const roverAppURL = getBrandRedirectUrl(RoverBrand.Rover, '/app');
  const insightsDashboardURL = getBrandRedirectUrl(RoverBrand.Rover, '/provider-profile/scores/');
  const privacyPolicyURL =
    countryConfig?.urls?.privacyPolicyUrl ||
    getBrandRedirectUrl(RoverBrand.Rover, '/terms/privacy/');
  const meetGreetsURL = getBrandRedirectUrl(
    RoverBrand.Rover,
    '/blog/safety/meet-greets-quick-guide/'
  );
  const roverInboxURL = getBrandRedirectUrl(RoverBrand.Rover, '/account/inbox/');
  const profilePromoteURL = getBrandRedirectUrl(RoverBrand.Rover, '/provider-profile/promote');
  const starSitterURL = getBrandRedirectUrl(RoverBrand.Rover, '/star-sitter');
  const starSitterProgramURL = getBrandRedirectUrl(RoverBrand.Rover, '/star-sitter/');
  const emailMarketingSettingsURL = getBrandRedirectUrl(
    RoverBrand.Rover,
    '/account/profile/settings'
  );
  const providerProfileURL = getBrandRedirectUrl(RoverBrand.Rover, '/provider-profile');
  const adjustServicesAndRatesURL = getBrandRedirectUrl(RoverBrand.Rover, '/provider-profile/');
  const DEFAULT_ACTIVATION_DATE = t`April 8`;
  const ACTIVATION_DATES = {
    US: t`March 31`,
    ES: t`March 24`,
    CA: t`March 24`,
  } as const;
  const activationDate = countryConfig?.countryCode
    ? ACTIVATION_DATES[countryConfig.countryCode] ?? DEFAULT_ACTIVATION_DATE
    : DEFAULT_ACTIVATION_DATE;
  const isUS = countryConfig?.countryCode === 'US';

  const roverSitter101: FAQCategory = {
    id: 'sitter-101',
    label: 'Sitter 101',
    type: FAQCategoryType.LIST,
    description: t`As you transition from Cat in a Flat to Rover, you’ll notice many similarities—along with a few differences in how bookings, payments, and visibility work. Here’s a quick overview to help you get comfortable on the platform:`,
    items: [
      {
        icon: '\u{1F43E}',
        label: t`Your profile & account:`,
        text: (
          <Trans>
            Your Cat in a Flat profile, reviews, bookings, and messages have moved to Rover. To
            appear in search and receive new requests on both Cat in a Flat and Rover, complete your{' '}
            {isUS ? t`Background Check` : t`Identity Verification`} and review your services, rates,
            and settings.
          </Trans>
        ),
      },
      {
        icon: '\u{1F4B3}',
        label: t`Getting paid:`,
        text: (
          <Trans>
            Rover handles secure payments for you. Once your payout method is set up, you'll receive
            payment two days after a booking ends. Bookings made on Cat in a Flat prior to{' '}
            {activationDate}, 2026 will continue to be paid out through Cat in a Flat under their
            existing payment terms.
          </Trans>
        ),
      },
      {
        icon: '\u{1F4F1}',
        label: t`The Rover app:`,
        text: (
          <Trans>
            Use the{' '}
            <Link asInlineLink href={roverAppURL} target="_blank">
              Rover app
            </Link>{' '}
            to manage requests, respond to pet parents, and keep track of upcoming stays. Staying
            responsive helps you stay competitive and visible. Check{' '}
            <Link asInlineLink href={insightsDashboardURL} target="_blank">
              your Insights dashboard
            </Link>{' '}
            to monitor performance metrics that influence your visibility in search.
          </Trans>
        ),
      },
      {
        icon: '\u{1F4C5}',
        label: t`Bookings & calendar management:`,
        text: (
          <Trans>
            All new booking requests and modifications are managed on Rover and subject to Rover's{' '}
            <Link asInlineLink href={`${termsOfServiceURL}?ref=joining-forces`} target="_blank">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link asInlineLink href={privacyPolicyURL} target="_blank">
              Privacy Policy
            </Link>
            . Keep your availability calendar up to date so pet parents can see when you're
            available. We also highly recommend setting up{' '}
            <Link asInlineLink href={meetGreetsURL} target="_blank">
              Meet & Greets
            </Link>{' '}
            before bookings!
          </Trans>
        ),
      },
      {
        icon: '\u{1F4AC}',
        label: t`Messaging, photos, & reviews:`,
        text: (
          <Trans>
            All communication happens in your{' '}
            <Link asInlineLink href={roverInboxURL} target="_blank">
              Rover inbox
            </Link>{' '}
            . During stays, use your inbox to send photo and message updates, share{' '}
            <Link asInlineLink href={roverCardsUrl} target="_blank">
              report cards
            </Link>{' '}
            after drop-in visits, as well as arrange Meet & Greets. After bookings, we'll ask your
            clients to leave reviews which can strengthen your profile, and you can easily share
            your profile with prospective clients via your{' '}
            <Link asInlineLink href={profilePromoteURL} target="_blank">
              unique profile URL
            </Link>
            .
          </Trans>
        ),
      },
      {
        icon: '\u{2B50}',
        label: t`Star Sitter status:`,
        text: (
          <Trans>
            <Link asInlineLink href={starSitterURL} target="_blank">
              The Star Sitter program
            </Link>{' '}
            recognizes sitters who consistently provide highly-rated care for pets and responsive,
            reliable service for pet parents on Rover. Sitters with this status earn a badge for
            their profile that helps them stand out in search and attract more bookings.
          </Trans>
        ),
      },
      {
        icon: '\u{1F6E1}',
        label: t`Trust, safety, & the Rover Guarantee:`,
        text: (
          <Trans>
            Bookings made on and paid through Rover are backed by the{' '}
            <Link asInlineLink href={roverGuaranteeURL} target="_blank">
              Rover Guarantee
            </Link>
            . Keeping communication and payments on-platform protects the pets in your care with up
            to {vetCareReimbursementAmount} in reimbursement for eligible vet care.
          </Trans>
        ),
      },
      {
        icon: '\u{1F4C8}',
        label: t`Growing your business on Rover:`,
        text: (
          <Trans>
            Your reviews, response time, and calendar availability influence your{' '}
            <Link asInlineLink href={improveMyPositionInSearchUrl} target="_blank">
              visibility in search
            </Link>
            . You can search. You can{' '}
            <Link asInlineLink href={adjustServicesAndRatesURL} target="_blank">
              adjust
            </Link>{' '}
            your services and rates anytime to match your goals.
          </Trans>
        ),
      },
      {
        icon: '\u{1F4DA}',
        label: t`Helpful resources:`,
        text: (
          <Trans>
            Explore the{' '}
            <Link asInlineLink href={sitterResourceCenterURL} target="_blank">
              Sitter Resource Center
            </Link>
            ,{' '}
            <Link asInlineLink href={roverBlogURL} target="_blank">
              Rover blog
            </Link>
            , and{' '}
            <Link asInlineLink href={helpCenterURL} target="_blank">
              Help Center
            </Link>{' '}
            for guides, best practices, and answers to common questions. You can also get
            educational emails—including Sitter Download, our monthly newsletter packed with tips,
            updates, and tools to help you grow. Be sure{' '}
            <Link asInlineLink href={emailMarketingSettingsURL} target="_blank">
              you're opted in
            </Link>{' '}
            to receive marketing emails from Rover to get these helpful resources.
          </Trans>
        ),
      },
      {
        icon: '\u{1F436}',
        label: t`One more thing:`,
        text: (
          <Trans>
            We're so glad to have your experience and love for cats on Rover. If you also offer dog
            care, you can expand{' '}
            <Link asInlineLink href={providerProfileURL} target="_blank">
              your profile
            </Link>{' '}
            to include those services at any time.
          </Trans>
        ),
      },
    ],
  };

  return {
    title: 'Frequently Asked Questions',
    subtitle: 'Learn more',
    type: FAQCategoryType.TABBED,
    categories: [
      {
        id: 'general',
        label: t`General`,
        type: FAQCategoryType.ACCORDION,
        items: [
          {
            question: t`Why have Cat in a Flat and Rover come together?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>
                    Rover and Cat in a Flat share the same mission: To make life better for cats and
                    the people who love them, and we can make an even bigger impact together. Coming
                    together makes it easier for cat parents to get trusted care for their cats, for
                    cat sitters to connect with more local cat parents, and most importantly, for
                    cats to get the loving care they deserve.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Don't worry! Cat in a Flat isn't going anywhere, but is simply joining the Rover
                    Group where we can continue to make life better for cats and the people who love
                    them.
                  </Trans>
                </Paragraph>
              </>
            ),
            isOpenByDefault: true,
          },
          {
            question: t`What is happening with Cat in a Flat and Rover?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>
                    All Cat in a Flat cat parents and sitters are invited to activate their accounts
                    and accept the Rover terms and privacy statement, which will apply to your Cat
                    in a Flat experience going forward. This can be done in just a few steps.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    What to expect as a cat parent: You'll continue to use Cat in a Flat with the
                    addition of new features and an upgraded product experience. Plus, you'll be
                    able to book with an even bigger selection of experienced cat sitters.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    What to expect as a sitter: You'll manage existing bookings on Cat in a Flat up
                    until {activationDate}, 2026, after which we encourage you to manage your sitter
                    business on Rover—that way, all your info will live in one place. This means
                    your requests and profile settings are managed on Rover. But don't worry, you
                    will still be able to be reached by both Cat in a Flat and Rover cat parents.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    What to expect as a sitter and a cat parent: If you still want, you can create
                    bookings on Cat in a Flat however, your sitter business will be managed on
                    Rover.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    You'll still be able to access your Cat in a Flat information—including your
                    history, messages, and any upcoming bookings—by logging into your profile on Cat
                    in a Flat, where you will then be taken to a landing page to complete activating
                    your Rover account.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`How do I make sure my Cat in a Flat profile information shows up in my Rover profile?`,
            answer: (
              <>
                <UnorderedList>
                  <ListItem>
                    <Trans>
                      On the activation day {activationDate}, 2026, you will receive an email from
                      the Cat in a Flat team. Click on the link and you'll be redirected to{' '}
                      <Link size="100" asInlineLink href={activationUrl} target="_blank">
                        {activationUrl}
                      </Link>
                      .
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Enter your Cat in a Flat email and password if prompted. If you already have a
                      Rover account with the same email, please use your Rover credentials as we
                      will have combined the accounts for you.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      You will get redirected to a landing page where you will be prompted to agree
                      to Rover's Terms of Service and Privacy Statement.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      That's it! Your Cat in a Flat profile—including all of your existing reviews,
                      messages, and bookings (past, pending, current, and future)—will show up
                      automatically.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      We strongly recommend you check over your profile to fill in any additional
                      information or update your profile.
                    </Trans>
                  </ListItem>
                </UnorderedList>

                <Paragraph fontWeight="semibold">
                  <Trans>
                    What happens to my Cat in a Flat profile if I don't choose to accept Rover's
                    Terms of Service?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    You'll no longer be able to access your Cat in a Flat profile on Cat in a Flat's
                    site or app. If you have ongoing or future bookings as a cat parent you will be
                    charged as usual. If you're a sitter, you will be paid out for ongoing or future
                    bookings as usual also. Contact our support team at{' '}
                    <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                      support@catinaflat.com
                    </Link>{' '}
                    if there is an issue.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`Booked Stays & Payment`,
            answer: (
              <>
                <Paragraph fontWeight="semibold">
                  <Trans>
                    I have a booked stay on Cat in a Flat for {activationDate}, 2026 or later. What
                    should I do?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    To manage a stay, activate your Rover account by logging in to Cat in a Flat
                    with your usual username and password. As an owner, you will remain on Cat in a
                    Flat and as a Sitter, you will go to Rover to continue. If you don't activate
                    your Rover account, your booking will have to be managed directly by our support
                    team at{' '}
                    <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                      support@catinaflat.com
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold">
                  <Trans>
                    I have a booked stay on Cat in a Flat for {activationDate}, 2026 or later. How
                    do I receive payment?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    All stays booked on Cat in a Flat before {activationDate}, 2026 will be paid out
                    by Cat in a Flat's payment terms, regardless of whether or not you've activated
                    your Rover account. Stays booked after you activate your account will be paid
                    out on Rover's payment terms - which is roughly 2 days after booking ends.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold">
                  <Trans>
                    I have a booked stay on Cat in a Flat for {activationDate}, 2026 or later and I
                    need to modify or cancel this booking. How can I do that?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    You can modify or cancel bookings made before {activationDate}, 2026 on Cat in a
                    Flat. On or after {activationDate}, 2026, the easiest way to modify or cancel
                    the booking is to activate your Rover account. Sitters will automatically be
                    redirected to Rover and pet parents will be redirected to Cat in a Flat.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Your booking details will be available on the respective platform, and you can
                    submit a modification request for your booking. If requested changes involve
                    altering dates, you will be asked to rebook the stay entirely.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold">
                  <Trans>
                    I have a stay booked on Cat in a Flat for {activationDate}, 2026 or later and I
                    don't intend to activate my account on Rover.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    We're sorry to hear that. Contact our support team at{' '}
                    <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                      support@catinaflat.com
                    </Link>{' '}
                    if you want to cancel or modify the booking. If you still want to keep the
                    booking, there is nothing that you need to do. However, if you do not activate,
                    you will not be able to contact the other party via the Cat in a Flat platform
                    after {activationDate}, 2026.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`What if I use my Cat in a Flat account as both a cat parent and a sitter?`,
            answer: (
              <Paragraph>
                <Trans>
                  Activating your Rover account will also activate your booking history as a cat
                  parent on Rover. You can continue to use your new Rover profile as a sitter or a
                  pet parent, just as you did on Cat in a Flat. Or, if you wish, you can still book
                  and message sitters on Cat in a Flat as a cat parent. These bookings will be
                  subject to Rover's Terms of Service.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`How does this benefit cat parents?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>
                    Joining forces with Rover makes the Cat in a Flat experience even better. Here's
                    how:
                  </Trans>
                </Paragraph>
                <UnorderedList>
                  <ListItem>
                    <Trans>
                      Even more cat sitters to choose from: Find the perfect match for your cat from
                      a worldwide network of screened cat sitters, with millions of five-star
                      reviews from cat parents like you.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Real-time updates: Receive photos and stay connected with your cat throughout
                      every booking with Cat in a Flat Report Cards.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Trust & Safety Support: We're here for you before, during, and after your
                      booking. Enjoy access to our knowledgeable Trust & Safety team when you need
                      it.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Rover Guarantee: Your pets are protected by up to {vetCareReimbursementAmount}{' '}
                      in eligible emergency vet care with the Rover Guarantee.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Secure, predictable payments: Your payment is processed when the booking is
                      confirmed, so everything is taken care of before you hand over your keys.
                    </Trans>
                  </ListItem>
                </UnorderedList>
              </>
            ),
          },
          {
            question: t`How does this benefit sitters?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>As a cat sitter, joining Rover comes with perks like:</Trans>
                </Paragraph>
                <UnorderedList>
                  <ListItem>
                    <Trans>
                      A thriving and even larger community: You're part of a global network of
                      likeminded cat sitters and cat parents.
                    </Trans>
                  </ListItem>
                  {!isUS && (
                    <ListItem>
                      <Trans>
                        More earning potential: With access to more services, a larger community of
                        cat parents (more than 6 million of them!), and a lower sitter service fee
                        than before, you can keep more of what you earn and grow your business over
                        time.
                      </Trans>
                    </ListItem>
                  )}
                  <ListItem>
                    <Trans>
                      More service offerings: Grow your pet care offerings with additional services
                      like overnight boarding and weekly care bookings.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      User-friendly features: The Rover app makes managing your business a
                      snap—share photos and messages with cat parents, field new requests, get paid,
                      and more.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Expert support and the Rover Guarantee: We're here for you before, during, and
                      after your booking.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Clear payout timing: Payouts are released a few days after a booking ends,
                      following Rover’s standard policy—so you know when to expect your earnings,
                      even on longer stays.
                    </Trans>
                  </ListItem>
                </UnorderedList>
              </>
            ),
          },
        ],
      },
      {
        id: 'cat_parents',
        label: t`Cat Parents`,
        type: FAQCategoryType.ACCORDION,
        items: [
          {
            question: t`Why should I activate my account on Rover?`,
            answer: (
              <Paragraph>
                <Trans>
                  By activating your account on Rover, you'll have access to a larger network of
                  experienced cat sitters, protections like the Rover Guarantee, and the same easy
                  booking process with the same Cat in a Flat style we know you love. Don't worry!
                  Cat in a Flat isn't going anywhere, it is just joining the Rover Group. We're
                  combining our resources to invest in the cat people who make our communities so
                  vibrant.
                </Trans>
              </Paragraph>
            ),
            isOpenByDefault: true,
          },
          {
            question: t`What does "activate my account" mean?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>
                    For cat parents, activation is the quick and easy process of agreeing to the
                    updated Terms of Service. Don't worry, you don't need to do anything before the
                    activation date. On that day, you'll get an email with instructions or you can
                    simply visit the Cat in a Flat site or app to start your activation.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Activating your account will allow you continuing access to your Cat in a Flat
                    profile, messages, and your past, pending, current, and future bookings.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>If you don't already have a Rover account:</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    When you activate your account, it will have the information from your Cat in a
                    Flat account along with your Cat in a Flat bookings (past, pending, current, and
                    future) and messages.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>If you already have a Rover account:</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Your Cat in a Flat bookings (past, pending, current, and future) and messages
                    will be merged with your existing Rover account. You can log in to Cat in a Flat
                    or Rover with the same Rover credentials.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`What are some key things I need to be aware of?`,
            answer: (
              <UnorderedList>
                <ListItem>
                  {isUS ? (
                    <Trans>
                      Instead of a fixed Cat in a Flat fee, there’s now an 11% service fee tied to
                      your booking total, which you can learn more about in depth{' '}
                      <Link size="100" asInlineLink href={`${serviceFeesUrl}`} target="_blank">
                        here
                      </Link>
                      . This update helps us power the support and safety tools that keep our
                      community secure and your kitties safe.
                    </Trans>
                  ) : (
                    <Trans>
                      Instead of a fixed Cat in a Flat fee, there’s now an 15% service fee tied to
                      your booking total, which you can learn more about in depth{' '}
                      <Link size="100" asInlineLink href={`${serviceFeesUrl}`} target="_blank">
                        here
                      </Link>
                      . This update helps us power the support and safety tools that keep our
                      community secure and your kitties safe.
                    </Trans>
                  )}
                </ListItem>
                <ListItem>
                  <Trans>
                    When booking a service, you’ll first see a pending authorization charge for the
                    total price of the booking. Once both you and your sitter confirm and the
                    booking is finalized, that charge will go through.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    Overnight house sitting or boarding stays and daytime drop-in visits are now
                    separate services, so you'll need to book them separately if you want both. See
                    FAQ &quot;What if I have an existing booking with mixed services?&quot; below to
                    learn more.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>You will continue to receive emails from Cat in a Flat</Trans>
                </ListItem>
              </UnorderedList>
            ),
          },
          {
            question: t`What if I have an existing booking with mixed services?`,
            answer: (
              <UnorderedList>
                <ListItem>
                  <Trans>
                    Mixed services in a single booking are no longer supported, so overnight house
                    sitting or boarding stays and daytime drop-in visits will need to be booked
                    separately.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    If you have an existing booking with mixed services, in your new Cat in a Flat
                    inbox they will show up as separate conversations. The pricing will be split
                    evenly.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    If you do not modify or cancel the booking, it will continue as normal.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    If you do need to modify or cancel, you will have to send a modification or
                    cancellation request and all services within the booking will be cancelled.
                  </Trans>
                </ListItem>
              </UnorderedList>
            ),
          },
          {
            question: t`How do I transfer my Cat in a Flat profile information to Rover?`,
            answer: (
              <UnorderedList>
                <ListItem>
                  <Trans>
                    On the activation day, you will receive an email from the Cat in a Flat team.
                    Follow the link and you'll be redirected to{' '}
                    <Link size="100" asInlineLink href={activationUrl} target="_blank">
                      {activationUrl}
                    </Link>
                    .
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    Enter your Cat in a Flat email and password if prompted. If you already have a
                    Rover Account with the same email, please use your Rover credentials!
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    You will get redirected to a landing page where you will be prompted to agree to
                    Rover's Terms of Service.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    That's it! Your Cat in a Flat profile—including all of your existing messages
                    and bookings (past, pending, current, and future)—will be published
                    automatically.
                  </Trans>
                </ListItem>
                <ListItem>
                  <Trans>
                    We strongly recommend you check over your profile to fill in any additional
                    information or update your profile.
                  </Trans>
                </ListItem>
              </UnorderedList>
            ),
          },
          {
            question: t`How will my Cat in a Flat profile appear once I activate my account?`,
            answer: (
              <>
                <Paragraph mb="0x">
                  <Trans>Your profile will include all the same information, namely:</Trans>
                </Paragraph>
                <UnorderedList>
                  <ListItem>
                    <Trans>Basic account info (Name, Contact Info, Location, etc.)</Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>Cat information</Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>Message history</Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>Booking history</Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>Communication preferences</Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>Emergency contacts</Trans>
                  </ListItem>
                </UnorderedList>
                <Paragraph>
                  <Trans>
                    Not every profile section or field will be exactly the same, but your
                    information will all be there!
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`What if I am a sitter and a Cat owner?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>
                    As a sitter, you'll manage your existing bookings, sitter profile, and settings
                    on Rover. You'll still be reachable by both Cat in a Flat cat parents and Rover
                    cat parents.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    As a cat parent, you can continue booking services on Cat in a Flat, however you
                    can also book the same sitters on Rover, if you prefer. In either case, Rover's
                    Terms of Service and Rover Guarantee will apply.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`When will I need to activate my account?`,
            answer: (
              <Paragraph>
                <Trans>
                  We highly recommend activating your Rover account on {activationDate}, 2026.
                  Starting {activationDate}, 2026, you will no longer be able to create new bookings
                  or manage existing or future bookings if you do not activate your account.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Can I pay my sitter in cash?`,
            answer: (
              <Paragraph>
                <Trans>
                  No. Paying on Cat in a Flat is still required per Rover's{' '}
                  <Link
                    asInlineLink
                    href={`${termsOfServiceURL}?ref=joining-forces`}
                    target="_blank"
                  >
                    Terms of Service
                  </Link>
                  , and is essential to supporting safer experiences. Additionally, only stays
                  booked on Cat in a Flat are covered by the{' '}
                  <Link asInlineLink href={roverGuaranteeURL} target="_blank">
                    Rover Guarantee
                  </Link>
                  .
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Can I communicate with my sitter outside of my Rover or Cat in a Flat accounts?`,
            answer: (
              <Paragraph>
                <Trans>
                  No. Communicating on Cat in a Flat or Rover is required per Rover's{' '}
                  <Link
                    asInlineLink
                    href={`${termsOfServiceURL}?ref=joining-forces`}
                    target="_blank"
                  >
                    Terms of Service
                  </Link>{' '}
                  and is essential to supporting safer experiences. If you communicate with your
                  sitter via other methods, you may not be covered by the{' '}
                  <Link asInlineLink href={roverGuaranteeURL} target="_blank">
                    Rover Guarantee
                  </Link>{' '}
                  and we may be unable to help if something goes wrong.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Will my existing bookings on Cat in a Flat carry over?`,
            answer: (
              <Paragraph>
                <Trans>
                  Yes. Once you activate your account and accept the Rover Terms of Service, you'll
                  be able to view and manage all of your past, pending, current, and future Cat in a
                  Flat bookings as usual!
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`I have a stay booked on Cat in a Flat for ${activationDate}, 2026 or later. What should I do?`,
            answer: (
              <Paragraph>
                <Trans>
                  You can continue to manage your stay as you normally would on Cat in a Flat by
                  activating your account and accept the Rover Terms of Service. If you don't
                  activate your account, your booking will need to be managed directly by your
                  sitter or by contacting customer support at{' '}
                  <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                    support@catinaflat.com
                  </Link>
                  . You will still be charged for the booking 24 hours before the start date.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`I have a stay booked on Cat in a Flat for ${activationDate}, 2026 or later and I don't intend to activate my Rover account. What should I do?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>
                    While we'd love to have you join our community, you certainly don't have to
                    activate your Rover account if you don't want to. Here's what will happen if you
                    decline to activate: If you don't need to make any modifications to your stay,
                    no action is needed. Simply keep in touch with your sitter via their WhatsApp or
                    contact Cat in a Flat support at{' '}
                    <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                      support@catinaflat.com
                    </Link>
                    . If you do need to make changes to your stay, activating your account will
                    allow you to manage changes on Cat in a Flat.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Please note: Your booking will not be eligible for protection under the Rover
                    Guarantee if you choose not to activate your account and accept Rover's Terms of
                    Service.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`Are my booking confirmation numbers going to stay the same?`,
            answer: (
              <Paragraph>
                <Trans>
                  Yes, your booking confirmation numbers will stay the same and you can use them to
                  reference specific stays when contacting our support team.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Will I be able to find sitters I've booked with before?`,
            answer: (
              <Paragraph>
                <Trans>
                  Yes, Cat in a Flat sitters are being welcomed to Rover, and you'll be able to see
                  your historical bookings and messages on your profile if they accept Rover's Terms
                  of Service.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Are sitter cancellation policies the same on Cat in a Flat?`,
            answer: (
              <Paragraph>
                <Trans>
                  Sitters have more cancellation policy options now that Cat in a Flat has joined
                  the Rover Group. They can set 7 day, 3 day or 24 hour cancellation policies. We
                  recommend reviewing the various sitter cancellation policies{' '}
                  <Link asInlineLink href={sitterCancellationPoliciesUrl} target="_blank">
                    here
                  </Link>{' '}
                  when you activate your account.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Are bookings on Cat in a Flat still covered by a guarantee similar to what I'm used to?`,
            answer: (
              <>
                <Paragraph mb="0x">
                  <Trans>
                    Yes! Safety is a top priority for both Cat in a Flat and Rover. Once you
                    activate your account and accept Rover's terms of service, every booking you
                    make through Cat in a Flat will be backed by the Rover Guarantee, which
                    includes:
                  </Trans>
                </Paragraph>
                <UnorderedList>
                  <ListItem>
                    <Trans>
                      Up to {vetCareReimbursementAmount} in vet care reimbursement for eligible
                      claims.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Coverage for certain property damage to the cat owner's home caused by a
                      sitter.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>Certain out-of-pocket medical costs for 3rd party injuries.</Trans>
                  </ListItem>
                </UnorderedList>
                <Paragraph>
                  <Trans>
                    When you're away, you can feel secure in the knowledge that your cat's health
                    and wellness are covered. You can learn more about the Rover Guarantee{' '}
                    <Link asInlineLink href={roverGuaranteeURL} target="_blank">
                      here
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            ),
          },
        ],
      },
      {
        id: 'cat_sitters',
        label: t`Cat Sitters`,
        type: FAQCategoryType.ACCORDION,
        items: [
          {
            question: t`What does "activate my account" mean?`,
            answer: (
              <>
                <Paragraph>
                  <Trans>
                    Activation is the quick and easy process of enabling your Cat in a Flat account
                    on Rover. Activating your account will immediately populate your Cat in a Flat
                    profile, reviews, messages, and your past, pending, current, and future bookings
                    on the Rover platform.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>If you already have a Rover sitter profile:</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Your Cat in a Flat bookings (past, pending, current, and future), messages, and
                    reviews will be merged with your existing Rover account. Your Rover sitter
                    profile settings will remain the same and take precedence over your Cat in a
                    Flat ones.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>If you don't already have a Rover sitter profile:</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    When you activate your new Rover account, it'll have the information from your
                    Cat in a Flat account along with your Cat in a Flat bookings (past, pending,
                    current, and future), messages, and reviews.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>If you have a sitter and owner profile:</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    You will manage your existing sitter bookings, sitter profile and settings on
                    Rover. Nonetheless, you can still create bookings on the Cat in a Flat platform.
                    You will still be able to be reached by Cat in a Flat owners and Rover cat
                    owners.
                  </Trans>
                </Paragraph>
              </>
            ),
            isOpenByDefault: true,
          },
          {
            question: t`Once you activate your Rover account`,
            answer: (
              <>
                <Paragraph>
                  <Trans>What are the first things I should do after I activate my account?</Trans>
                </Paragraph>
                <UnorderedList>
                  <ListItem>
                    <Trans>
                      Importantly, to be able to receive new requests, 'Identity Verification' will
                      have to be filled out. Your payout details can be added under &quot;Receive
                      payments&quot;. There should also be a banner on the top of the screen leading
                      to this page.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      We recommend double checking your profile and settings for each service,
                      including rates, booking preferences, and cancellation policies. Review the
                      information that cat parents see on your profile under the &quot;Details&quot;
                      section.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Ensure your upcoming availability calendar is up-to-date as some things might
                      be slightly different.{' '}
                      <Link size="100" asInlineLink href={manageMyCalendarUrl} target="_blank">
                        This article
                      </Link>{' '}
                      provides information on how to do so.
                    </Trans>
                  </ListItem>
                  <ListItem>
                    <Trans>
                      Consider reaching out to past cat parents to check if they have activated
                      their account on Rover via your inbox. Let them know that they can start
                      booking with you on Rover. If the cat parent has not yet activated, consider
                      encouraging cat clients to activate so that they can continue getting your
                      messages.
                    </Trans>
                  </ListItem>
                </UnorderedList>
              </>
            ),
          },
          {
            question: t`What happens to my Cat in a Flat profile once I activate my account?`,
            answer: (
              <Paragraph>
                <Trans>
                  Your Cat in a Flat profile still exists on Cat in a Flat, however only as a cat
                  parent Your email, password, and basic profile details will still appear. You can
                  still book services, communicate, and change your owner profile on Cat in a Flat!
                  In either case, Rover's Terms of Service will apply. You will also now receive
                  emails from Rover.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Why should I activate my account?`,
            answer: (
              <Paragraph>
                <Trans>
                  Activating your account gets you access to both new and existing clients, an even
                  bigger network of cat parents, industry leading features for managing your
                  business, the Rover Guarantee with every booking, knowledgeable Trust & Safety
                  support, and much more. Going forward, the only way to continue offering pet care
                  services to Cat in a Flat clients is to activate your account with Rover.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`What information from my Cat in a Flat sitter profile will appear on Rover once I activate my Rover account?`,
            answer: (
              <>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans> If you already have a Rover sitter profile:</Trans>
                </Paragraph>
                <Box pl="2x" mb="4x">
                  <Box>
                    <Text fontWeight="semibold">
                      <Trans>Reviews:</Trans>
                    </Text>{' '}
                    <Text>
                      <Trans>
                        Your Cat in a Flat reviews will be combined with your Rover reviews on your
                        existing Rover profile.
                      </Trans>
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold">
                      <Trans>Past, pending, current, and future bookings:</Trans>
                    </Text>{' '}
                    <Text>
                      <Trans>
                        You'll be able to view all of your past, pending, current, and future Cat in
                        a Flat bookings on Rover. Any bookings accepted on Cat in a Flat will still
                        be paid out through Cat in a Flat.
                      </Trans>
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold">
                      <Trans>Messages with clients:</Trans>
                    </Text>{' '}
                    <Text>
                      <Trans>
                        After you activate, all of your messages with past and current clients will
                        appear in your Rover inbox, which you can find by clicking on your name in
                        the top right corner when you log in.
                      </Trans>
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold">
                      <Trans>Sitter Rates:</Trans>
                    </Text>{' '}
                    <Text>
                      <Trans>
                        If you have different sitter rates on Cat in a Flat and Rover, your Rover
                        sitter rate will be kept.
                      </Trans>
                    </Text>
                  </Box>
                  <Box>
                    <Text fontWeight="semibold">
                      <Trans>Sitter Service Fee:</Trans>
                    </Text>{' '}
                    <Text>
                      <Trans>
                        Your service fees will change to the Rover service fees for all bookings
                        made after {activationDate}, 2026.
                      </Trans>
                    </Text>
                  </Box>
                </Box>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>If you don't already have a Rover sitter profile:</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Your Cat in a Flat information will appear on your new Rover profile, including:
                    your reviews (past, pending, current, and future bookings), sitter fee
                    percentage, your profile description, calendar availability, booking
                    preferences, messages, services offered, and rates.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    We recommend reviewing your profile after activation to make sure it's up to
                    date.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`What's the impact on my business?`,
            answer: (
              <>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>Do I now have to provide dog services?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    It is entirely up to you if you want to provide care for dogs, for cats, or for
                    both!
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>How will my position in search be impacted?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    All of your ratings and reviews from Cat in a Flat will be recognized by Rover's
                    search page and rankings. We recommend you update your profile and calendar
                    availability to put your best foot forward. Go{' '}
                    <Link asInlineLink href={improveMyPositionInSearchUrl} target="_blank">
                      here
                    </Link>{' '}
                    to learn more about how to improve your listing's visibility in search.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    We also have a programme called 'Star Sitter' to help you shine in search. The
                    criteria to be eligible is{' '}
                    <Link asInlineLink href={starSitterProgramURL} target="_blank">
                      here
                    </Link>
                    , and you will begin eligibility once you activate your account. We don't
                    support Star Sitters status in all markets and are working on releasing it in
                    the future.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>How will I receive payment and what's changing with payments?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    After you activate your account, you'll need to set your payment preferences.
                    You can receive payment via bank transfer. As part of moving to Rover, there are
                    a few updates to how you get paid:
                  </Trans>
                </Paragraph>
                <Paragraph mb="0x">
                  <Trans>
                    Payout timing: You'll receive your payout on Rover two calendar days after the
                    completion of a booking, even on weekends and holidays. This is when Rover
                    releases the funds, but when they hit your account will depend on your bank’s
                    policies.
                  </Trans>
                </Paragraph>
                <Box mb="4x">
                  <UnorderedList>
                    <ListItem>
                      <Trans>
                        For example, a booking ending on a Friday will be paid out on Sunday. We
                        recommend adding your payout method as soon as possible as there is a
                        one-time 72 hour hold before we can transfer funds to a newly added payout
                        account.
                      </Trans>
                    </ListItem>
                  </UnorderedList>
                </Box>
                <Box mb="4x">
                  <UnorderedList>
                    <ListItem>
                      {isUS ? (
                        <Trans>
                          In the US, the sitter service fee on Rover is updated to be 20%. This
                          update helps us power the support and safety tools that keep our community
                          secure and kitties safe.
                        </Trans>
                      ) : (
                        <Trans>
                          Service fee: The sitter service fee on Rover is going down to 15%! This
                          means you keep a larger share of your earnings from every booking compared
                          to before.
                        </Trans>
                      )}
                    </ListItem>
                  </UnorderedList>
                </Box>
                <Paragraph mb="0x">
                  <Trans>
                    To learn more about the payment process, we suggest reading{' '}
                    <Link asInlineLink href={howDoIGetPaidForBookingsAndWhenUrl} target="_blank">
                      this article
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Bookings made before {activationDate}, 2026, will be paid out per Cat in a
                    Flat's payment schedule, bookings created after {activationDate}, 2026, will
                    follow Rover's payment schedule.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>Can I be paid in cash?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    No. Paying and communicating on Cat in a Flat and Rover is required per Rover's{' '}
                    <Link
                      asInlineLink
                      href={`${termsOfServiceURL}?ref=joining-forces`}
                      target="_blank"
                    >
                      Terms of Service
                    </Link>{' '}
                    and is essential to supporting safer experiences. Accepting payments outside of
                    the platform could lead to account suspensions for both the sitter and the cat
                    parent. Additionally, only stays booked on Cat in a Flat and Rover are covered
                    by the Rover Guarantee.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>Should I update my rate?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    That's up to you! If you're unsure, once you activate your account, you will
                    have the option of customizing your rates if you'd like to do so.{' '}
                    <Link
                      asInlineLink
                      href={howDoIManageMyRatesForAdditionalServicesUrl}
                      target="_blank"
                    >
                      Here's
                    </Link>{' '}
                    an article that explains how and why you may want to do this.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>
                    I've sent custom quotes on Cat in a Flat, will I still be able to do that?
                  </Trans>
                </Paragraph>
                <Paragraph mb="0x">
                  <Trans>Yes, it just works a little differently:</Trans>
                </Paragraph>
                <Box mb="4x">
                  <UnorderedList>
                    <ListItem>
                      <Trans>
                        If a client has never booked with you before, they must first send you a
                        booking request. You can then edit the rate and send the request back, and
                        they'll confirm the amended request.
                      </Trans>
                    </ListItem>
                    <ListItem>
                      <Trans>
                        If a client has booked with you before, you may initiate the reservation
                        request and adjust the rate as you see fit.
                      </Trans>
                    </ListItem>
                  </UnorderedList>
                  <Paragraph>
                    You can read more about this process{' '}
                    <Link
                      asInlineLink
                      href={howDoIModifyAPendingRequestBeforeBookingItUrl}
                      target="_blank"
                    >
                      here
                    </Link>
                    .
                  </Paragraph>
                </Box>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>
                    Which booking guarantee will apply after I activate my Rover account?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Your clients can book with confidence knowing that whether they book on Rover or
                    Cat in a Flat, their stays are backed by the Rover Guarantee. After{' '}
                    {activationDate}, 2026, upcoming bookings are backed by Rover guarantee even if
                    they were made on Cat in a Flat prior to activation. You can learn more about
                    the Rover Guarantee that's included with every booking{' '}
                    <Link asInlineLink href={roverGuaranteeURL} target="_blank">
                      here
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>What happens to my cancellation policy?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    When you activate your Rover account, it will be set to the Rover cancellation
                    policy that's most similar to your Cat in a Flat one. You can change your
                    policies at any time, and we encourage you to review the cancellation policy
                    definitions{' '}
                    <Link asInlineLink href={sitterCancellationPoliciesUrl} target="_blank">
                      here
                    </Link>{' '}
                    and adjust yours as you see fit.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>Will I get enough business on the Rover platform?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Cat in a Flat joining Rover means that your potential client base has
                    expanded—you'll be able to book with both Cat in a Flat cat parents and Rover
                    cat parents! By combining resources, we'll also be able to invest more in our
                    sitter community—from advertising and growing our customer base, to adopting new
                    technologies that'll help you run your business—which means more potential
                    customers for you.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`How do I transfer my Cat in a Flat profile information to Rover?`,
            answer: (
              <>
                <Paragraph mb="0x">
                  1.{' '}
                  <Trans>
                    On the activation day, you will receive an email from the Cat in a Flat team.
                    Follow the link and you'll be redirected to{' '}
                    <Link asInlineLink href={activationUrl} target="_blank">
                      {activationUrl}
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph mb="0x">
                  2.{' '}
                  <Trans>
                    Enter your Cat in a Flat email and password if prompted. If you already have a
                    Rover account with the same email, please use your Rover credentials as we will
                    have combined the accounts for you.
                  </Trans>
                </Paragraph>
                <Paragraph mb="0x">
                  3.{' '}
                  <Trans>
                    You will get redirected to a landing page where you will be prompted to agree to
                    Rover's Terms of Service.
                  </Trans>
                </Paragraph>
                <Paragraph mb="0x">
                  4.{' '}
                  <Trans>
                    That's it! Your Cat in a Flat profile—including all of your existing reviews,
                    messages, and bookings (past, pending, current, and future)—will be published
                    automatically on your Rover profile.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`What does this mean for clients I met on Cat in a Flat?`,
            answer: (
              <>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>How do I move my clients to Rover?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    We've notified your clients and provided them with the directions they'll need
                    to activate their accounts as well. We encourage you to stay in contact with
                    your clients via your Rover inbox as you activate your account. Our goal is to
                    encourage all of your clients to activate their accounts and continue booking
                    with you!
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>What should I tell my clients?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    We recommend sending them a link to your profile once you have activated your
                    account. Let them know they can message and book with you just as they did
                    previously once they activate their account.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>What happens with my client's booking fees?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Rover's fees will apply to all bookings made after {activationDate}, 2026. For
                    additional information about Rover's fees, please see this{' '}
                    <Link asInlineLink href={`${serviceFeesUrl}`} target="_blank">
                      help page
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>
                    Will bookings on Rover be covered by a guarantee similar to the one on Cat in a
                    Flat?
                  </Trans>
                </Paragraph>
                <Paragraph mb="0x">
                  <Trans>
                    Yes! Safety is a top priority for both Cat in a Flat and Rover. Every booking
                    made through Cat in a Flat is backed by the Rover Guarantee, which includes:
                  </Trans>
                </Paragraph>
                <Box mb="4x">
                  <UnorderedList>
                    <ListItem>
                      <Trans>
                        Up to {vetCareReimbursementAmount} in vet care reimbursement for eligible
                        claims.
                      </Trans>
                    </ListItem>
                    <ListItem>
                      <Trans>
                        Coverage for certain property damage to the cat owner's home caused by a
                        sitter.
                      </Trans>
                    </ListItem>
                    <ListItem>
                      <Trans>Certain out-of-pocket medical costs for 3rd party injuries</Trans>
                    </ListItem>
                  </UnorderedList>
                </Box>
                <Paragraph>
                  <Trans>
                    When your clients are away, they can feel secure in the knowledge that their
                    cat's health and wellness are covered. You can learn more about the Rover
                    Guarantee{' '}
                    <Link asInlineLink href={roverGuaranteeURL} target="_blank">
                      here
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>How do repeat clients find me, message me, and book with me?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    You can find your complete message history with your Cat in a Flat repeat
                    clients on your new Rover profile. To learn how to reach out to your repeat
                    clients,{' '}
                    <Link
                      asInlineLink
                      href={howDoIBookWithAnOwnerOrCreateARepeatBookingUrl}
                      target="_blank"
                    >
                      go here
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>How do new clients book with me?</Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    Once you activate your Rover account and fill in your payment details, your
                    profile will appear in Rover and Cat in a Flat search results. You can also
                    share your Rover profile URL with potential customers. To contact you, new
                    clients will send you a booking request.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`When will I need to activate my account?`,
            answer: (
              <Paragraph>
                <Trans>
                  We highly recommend activating your Rover account on {activationDate}, 2026.
                  Starting {activationDate}, 2026 , you will no longer be able to accept new
                  bookings or modify existing or future bookings until you activate your account.
                </Trans>
              </Paragraph>
            ),
          },
          {
            question: t`Booked Stays`,
            answer: (
              <>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>
                    I have a booked stay on Cat in a Flat for {activationDate}, 2026 or later. What
                    should I do?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    You can manage stays on Cat in a Flat up until {activationDate}, 2026. After
                    that day, to manage a stay, activate your Rover account by logging in to Cat in
                    a Flat with your usual username and password. You will then get redirected to
                    Rover. If you don't activate your Rover account, your booking will have to be
                    managed directly with our support team at{' '}
                    <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                      support@catinaflat.com
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>
                    I have a booked stay on Cat in a Flat for {activationDate}, 2026 or later. How
                    do I receive payment?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    All stays booked on Cat in a Flat before {activationDate}, 2026 will be paid out
                    by Cat in a Flat, regardless of whether or not you've activated your Rover
                    account.
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>
                    I have a booked stay on Cat in a Flat for {activationDate}, 2026 or later and I
                    need to modify or cancel this booking. How can I do that?
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans>
                    You can modify or cancel bookings made before {activationDate}, 2026 on Cat in a
                    Flat. After {activationDate}, 2026 the easiest way to modify or cancel the
                    booking is to activate your Rover account by logging in on Cat in a Flat. This
                    will automatically redirect you to Rover, where you can activate your Rover
                    account. Your booking details will be available on Rover, and you can interact
                    with your client from there. You can modify or cancel your booking directly on
                    Rover, or by contacting our support team at{' '}
                    <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                      support@catinaflat.com
                    </Link>
                    .
                  </Trans>
                </Paragraph>
                <Paragraph fontWeight="semibold" mb="0x">
                  <Trans>
                    I have a stay booked on Cat in a Flat for {activationDate}, 2026 or later and I
                    don't intend to activate my account on Rover.
                  </Trans>
                </Paragraph>

                <Paragraph>
                  <Trans>
                    We're sorry to hear that. Contact Cat in a Flat's operations team at{' '}
                    <Link asInlineLink href="mailto:support@catinaflat.com" target="_blank">
                      support@catinaflat.com
                    </Link>{' '}
                    if you want to cancel or modify the booking. If you still want to keep the
                    booking, there is nothing that you need to do. However, if you do not activate,
                    you will not be able to contact your client via the Cat in a Flat platform after
                    {activationDate}, 2026.
                  </Trans>
                </Paragraph>
              </>
            ),
          },
          {
            question: t`What if I use my Cat in a Flat account as both a cat parent and a sitter?`,
            answer: (
              <Paragraph>
                <Trans>
                  Activating your Rover account will also activate your booking history as a cat
                  parent on Rover. You can continue to use your new Rover profile as a sitter or a
                  pet parent, just as you did on Cat in a Flat. Or, if you wish, you can still book
                  and message sitters on Cat in a Flat as a cat parent. In either case, Rover's
                  Terms of Service will apply.
                </Trans>
              </Paragraph>
            ),
          },
        ],
      },
      ...(showSitter101 ? [roverSitter101] : []),
    ],
  };
};
